import { Match } from '../models';
import {
    MatchRepository,
    ParticipantRepository,
    TournamentRepository
} from '../repositories';
import { logger } from '@/src/core/utils';

export class GenerateRoundRobinBracketUseCase {
  constructor(
    private tournamentRepository: TournamentRepository,
    private participantRepository: ParticipantRepository,
    private matchRepository: MatchRepository
  ) {}

  async execute(tournamentId: string): Promise<void> {
    logger.debug('RoundRobinBracket', 'Starting bracket generation', { tournamentId });

    // Get tournament
    const tournament = await this.tournamentRepository.getById(tournamentId);
    if (!tournament) {
      throw new Error('Tournament not found');
    }

    if (tournament.status !== 'setup') {
      throw new Error('Tournament has already started');
    }

    // Get participants
    const participants = await this.participantRepository.getByTournamentId(tournamentId);

    if (participants.length < 2) {
      throw new Error('Need at least 2 participants to generate bracket');
    }

    logger.debug('RoundRobinBracket', 'Participants loaded', { count: participants.length });

    // Generate all possible matches (round robin)
    const matches: Omit<Match, 'id' | 'createdAt' | 'updatedAt'>[] = [];
    let matchNumber = 1;

    // Round robin: every participant plays every other participant once
    for (let i = 0; i < participants.length; i++) {
      for (let j = i + 1; j < participants.length; j++) {
        matches.push({
          tournamentId,
          round: 1, // All matches are in "round 1" for round robin
          matchNumber: matchNumber++,
          bracketType: 'winners', // Using 'winners' bracket type for round robin
          participant1Id: participants[i].id,
          participant2Id: participants[j].id,
          status: 'pending',
          winnerId: undefined,
          scoreParticipant1: undefined,
          scoreParticipant2: undefined,
          nextMatchId: undefined,
          loserNextMatchId: undefined,
        });
      }
    }

    logger.debug('RoundRobinBracket', 'Matches generated', { count: matches.length });

    // Create all matches
    for (const matchData of matches) {
      await this.matchRepository.create(matchData);
    }

    // Update tournament status
    await this.tournamentRepository.update(tournamentId, {
      status: 'in_progress',
    });

    logger.debug('RoundRobinBracket', 'Bracket generated successfully');
  }
}
